import {
  TPlatformPackageSetting,
  getPlatformIdsForSelected,
} from "#next/utils/shared";
import {
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { Immutable, castDraft } from "immer";
import { useEffect, useRef } from "react";
import { useImmerReducer } from "use-immer";

type Action = {
  name: "TogglePlatform" | "TogglePlatformPackage";
  payload: { id: string };
};

type State = Immutable<TPlatformPackageSetting[]>;

const getValidationErrors = (
  state: State,
): Record<"platforms", string | null> => {
  const platformsAreValid =
    state.some((platformPackage) => platformPackage.isSelected) ||
    state
      .flatMap((platformPackage) =>
        platformPackage.platforms.map((platform) => platform.isSelected),
      )
      .some((isSelected) => isSelected);

  return {
    platforms: platformsAreValid ? null : "At least one platform is required",
  };
};

const getSelectedPlatformsSummary = (state: State) => {
  const selected: string[] = [];
  state.forEach((platformPackage) => {
    if (platformPackage.isSelected) {
      selected.push(platformPackage.name);
    } else {
      platformPackage.platforms.forEach((platform) => {
        if (platform.isSelected) {
          selected.push(platform.name);
        }
      });
    }
  });

  const text = selected.join(", ");

  return (
    <Tooltip title={text}>
      <Typography
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};

const isPlatformPackageIndeterminate = (
  platformPackage: Immutable<TPlatformPackageSetting>,
) => {
  return (
    !platformPackage.isSelected &&
    platformPackage.platforms.some((platform) => platform.isSelected)
  );
};

const throwIfNotFound = <T,>(array: T[], find: (item: T) => boolean) => {
  const found = array.find(find);
  if (!found) {
    throw new Error(`Item not found`);
  }
  return found;
};

const toggleSelected = <
  T extends Pick<TPlatformPackageSetting, "id" | "isSelected">,
>(
  items: Array<T>,
  payload: Action["payload"],
): T => {
  const item = throwIfNotFound(items, (item) => {
    return item.id === payload.id;
  });
  item.isSelected = !item.isSelected;
  return item;
};

export const PlatformPackageSelector = ({
  initialSettings,
  onSettingsChange,
}: {
  initialSettings: TPlatformPackageSetting[];
  onSettingsChange: ({
    platformPackages,
    settingsAreValid,
  }: {
    platformPackages: TPlatformPackageSetting[];
    settingsAreValid: boolean;
  }) => void;
}) => {
  // Used to stop the notifier effect running at mount. This way onSettingsChange
  // is only called from the first change onwards.
  const isMounted = useRef(false);
  const [state, dispatch] = useImmerReducer<State, Action>(
    (draft, { name, payload }) => {
      switch (name) {
        case "TogglePlatform": {
          toggleSelected(
            draft.flatMap((platformPackage) => platformPackage.platforms),
            payload,
          );
          break;
        }
        case "TogglePlatformPackage": {
          const platformPackage = toggleSelected(draft, payload);
          /* Set all platforms to false because either:
          1. The platform package has been unselected, which also means all its platform are unselected
          2. The platform package has been selected, and our logic requires that
          if a platform package is selected then all it's platforms are unselected, because it's not valid
          to have both the platform package selected and some number of children selected. We don't set all
          platforms to true in this scenario because we want to remove all platforms entries in the DB and create one
          for the package.
          */
          platformPackage.platforms.forEach(
            (platform) => (platform.isSelected = false),
          );
          break;
        }
        default: {
          const _exhaustivenessCheck: never = name;
          return _exhaustivenessCheck;
        }
      }
    },
    initialSettings,
  );

  const validationErrors = getValidationErrors(state);

  useEffect(() => {
    if (isMounted.current) {
      const settingsAreValid = Object.values(validationErrors).every(
        (error) => error === null,
      );
      onSettingsChange({
        platformPackages: structuredClone(state) as TPlatformPackageSetting[],
        settingsAreValid,
      });
    } else {
      isMounted.current = true;
    }
  }, [state]);

  const { platformIds, platformPackageIds } = getPlatformIdsForSelected(
    castDraft(state),
    false,
  );

  const selectValue = [...platformIds, ...platformPackageIds];

  return (
    <FormControl
      sx={{ width: 300 }}
      size="small"
      error={!!validationErrors.platforms}
    >
      <InputLabel id="platform-multiple-checkbox-label">Platforms</InputLabel>
      <Select
        labelId="platform-multiple-checkbox-label"
        size="small"
        id="platform-multiple-checkbox"
        multiple
        value={selectValue}
        input={<OutlinedInput label={"Platforms"} />}
        renderValue={() => getSelectedPlatformsSummary(state)}
        sx={{ pl: 0, lineHeight: "1em" }}
      >
        {state.map((platformPackage) => (
          <Grid container key={platformPackage.id} direction="column">
            <Grid item>
              <MenuItem
                disabled={!platformPackage.isAvailable}
                value={platformPackage.id}
                onClick={() =>
                  dispatch({
                    name: "TogglePlatformPackage",
                    payload: { id: platformPackage.id },
                  })
                }
              >
                <Checkbox
                  size="small"
                  checked={platformPackage.isSelected}
                  indeterminate={isPlatformPackageIndeterminate(
                    platformPackage,
                  )}
                />
                <ListItemText primary={platformPackage.name} />
              </MenuItem>
            </Grid>
            <Grid item sx={{ ml: 2 }}>
              {platformPackage.platforms.map((platform) => {
                return (
                  <MenuItem
                    key={platform.id}
                    value={platform.id}
                    disabled={!platform.isAvailable}
                    onClick={() =>
                      dispatch({
                        name: "TogglePlatform",
                        payload: { id: platform.id },
                      })
                    }
                  >
                    <Checkbox
                      size="small"
                      checked={
                        platformPackage.isSelected || platform.isSelected
                      }
                    />
                    <ListItemText
                      primary={`${platform.name}${
                        platform.isCurrentlyCollected ? "" : " (inactive)"
                      }`}
                    />
                  </MenuItem>
                );
              })}
            </Grid>
          </Grid>
        ))}
      </Select>
      <FormHelperText>{validationErrors.platforms}</FormHelperText>
    </FormControl>
  );
};
